import { faFileAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ContentType } from '@kcftech/prometheus-client';
import { AuthorizedImage, AuthorizedImageProps } from 'components/media/AuthorizedImage';
import { AuthorizedVideo } from 'components/media/AuthorizedVideo';
import styled from 'styled-components';
import { SecondaryText } from 'styles/theme_styles/text';
import { tokens } from 'styles/tokens';
import { scalingStyles } from './shared';

const NoPreviewPlaceholderWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.8rem;

    background-color: ${tokens.background.subtle};
    border: 1px solid ${tokens.border.default};
    border-radius: 8px;

    width: 35rem;
    height: 40rem;
`;

const NoPreviewPlaceholderIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    background-color: ${tokens.background.subtle};
    border: 1px solid ${tokens.border.default};
    border-radius: 4px;

    width: 100%;
    height: 100%;
`;

export type NoPreviewPlaceholderProps = Pick<
    AuthorizedImageProps,
    'className' | 'imageScaling'
>;
function NoPreviewPlaceholder({
    className,
    imageScaling,
    fileName,
}: NoPreviewPlaceholderProps & { fileName: string }) {
    return (
        <NoPreviewPlaceholderWrapper
            className={className}
            style={imageScaling ? scalingStyles[imageScaling] : undefined}
        >
            <FontAwesomeIcon icon={faFileAlt} size="8x" color={tokens.icon.secondary} />
            <SecondaryText className="text-center">
                <code>{fileName}</code>
            </SecondaryText>
            <SecondaryText style={{ fontWeight: 500 }}>
                No preview available
            </SecondaryText>
        </NoPreviewPlaceholderWrapper>
    );
}

export function NoPreviewPlaceholderIcon({
    className,
    fileName,
}: {
    className?: string;
    fileName: string;
}) {
    return (
        <NoPreviewPlaceholderIconWrapper
            className={className}
            title={`${fileName} - No preview available`}
        >
            <FontAwesomeIcon icon={faFileAlt} size="2x" color={tokens.icon.secondary} />
        </NoPreviewPlaceholderIconWrapper>
    );
}

export type MediaRendererProps = AuthorizedImageProps & {
    fileType: ContentType;
    fileName: string;
};

export function MediaRenderer({
    fileType,
    className,
    imageScaling,
    fileName,
    ...props
}: MediaRendererProps) {
    switch (fileType) {
        case ContentType.Video:
            return (
                <AuthorizedVideo
                    {...props}
                    imageScaling={imageScaling}
                    className={className}
                />
            );
        case ContentType.Picture:
            return (
                <AuthorizedImage
                    {...props}
                    imageScaling={imageScaling}
                    className={className}
                />
            );
        default:
            return (
                <NoPreviewPlaceholder
                    fileName={fileName}
                    className={className}
                    imageScaling={imageScaling}
                />
            );
    }
}
